import * as React from 'react';
import { DropDownContext, Options, DropdownProps } from '.';
import { Select } from '@mui/base';
import clsx from 'clsx';
import { styled } from '@mui/system';

type CustomSelectProps = Pick<
  DropdownProps,
  | 'disabled'
  | 'value'
  | 'onSelect'
  | 'multiple'
  | 'customSelectValue'
  | 'children'
  | 'size'
  | 'placeholder'
  | 'popperContainer'
  | 'validation'
  | 'customOnSelect'
>;

const StyledListbox = styled('div')({
  outline: 0,
});

const CustomSelect = React.forwardRef<HTMLSelectElement, CustomSelectProps>(function CustomSelect(props, ref) {
  const {
    disabled = false,
    value,
    onSelect,
    multiple = false,
    customSelectValue,
    placeholder = 'Select',
    popperContainer,
    validation,
    customOnSelect,
  } = props;
  const [selectValue, setSelectValue] = React.useState<any>(multiple ? [] : null);
  const ClearKey = 'apac_komodo_dropdown_clear';

  React.useEffect(() => {
    if (props.hasOwnProperty('value')) {
      setSelectValue(value);
    }
  }, [value]);

  const changeSelectValue = (newValue: any) => {
    if (customOnSelect) {
      return customOnSelect(newValue);
    }
    let selectedValue = newValue;

    const isStringOrArray = typeof newValue === 'string' || Array.isArray(newValue);
    if (isStringOrArray && newValue?.includes(ClearKey)) {
      selectedValue = Array.isArray(newValue) ? [] : null;
    }

    if (onSelect) {
      onSelect(selectedValue);
    }

    setSelectValue(selectedValue);
  };

  const renderValue = (option: Options & Options[]) => {
    if (customSelectValue) {
      return <span>{customSelectValue}</span>;
    }
    if (option === null || (Array.isArray(option) && option.length === 0) || option.value === ClearKey) {
      return <span>{placeholder}</span>;
    }

    if (multiple) {
      return option.map((item, index) => (
        <span key={index}>
          {item.label}
          {index !== option.length - 1 && ','}
        </span>
      ));
    } else {
      return <span>{option.label}</span>;
    }
  };

  const selectComponentsProps = {
    renderValue: (option: any) => renderValue(option) as any,
    disabled,
    slotProps: {
      root: {
        className: clsx(
          'c-dropdown__container',
          props.size === 'small' && 'c-dropdown__small',
          validation?.message && 'c-dropdown__container-validation--error',
          selectValue && selectValue?.length !== 0 && 'c-dropdown__container--has-value'
        ),
      },
      popper: {
        className: 'c-dropdown__options-container c-dropdown--active max-h-[300px] overflow-y-auto',
        disablePortal: popperContainer ? false : true,
        ...(popperContainer && { container: popperContainer }),
      },
    },
    slots: {
      root: 'div',
      listbox: StyledListbox,
    } as any,
    onChange: (e: any, newValue: any) => changeSelectValue(newValue),
    value: selectValue,
    multiple,
  };

  return (
    <DropDownContext.Provider value={{ selectValue, multiple }}>
      <Select {...selectComponentsProps}>{props.children}</Select>
    </DropDownContext.Provider>
  );
});

export default CustomSelect;
